import { useMemo, useCallback } from 'react';

/**
 * Hook pour gérer les permissions utilisateur dans le frontend.
 * Récupère les permissions depuis le localStorage.
 */
export const usePermissions = () => {
    const permissions = useMemo(() => {
        try {
            const stored = localStorage.getItem('permissions');
            return stored ? JSON.parse(stored) : [];
        } catch (e) {
            console.error('Erreur lors du parsing des permissions:', e);
            return [];
        }
    }, []);

    const typeUser = localStorage.getItem('typeUser');

    /**
     * Vérifie si l'utilisateur possède une permission spécifique.
     * 
     * @param {string} module - Le module (ex: 'clients', 'stock', 'devis'...)
     * @param {string} action - L'action (ex: 'read', 'create', 'update', 'delete')
     * @returns {boolean}
     */
    const hasPermission = useCallback((module, action = 'read') => {
        if (typeUser === 'admin') return true;
        if (typeUser === 'moderateur') {
            return permissions.includes(`${module}:${action}`);
        }
        return false;
    }, [typeUser, permissions]);

    /**
     * Vérifie si l'utilisateur a TOUTES les permissions (CRUD) sur un module.
     */
    const hasAllPermissions = useCallback((module) => {
        if (typeUser === 'admin') return true;
        if (typeUser !== 'moderateur') return false;

        const required = ['read', 'create', 'update', 'delete'];
        return required.every(action => permissions.includes(`${module}:${action}`));
    }, [typeUser, permissions]);

    /**
     * Vérifie si l'utilisateur a AU MOINS UNE permission sur un module.
     */
    const canAccessModule = useCallback((module) => {
        if (typeUser === 'admin') return true;
        if (typeUser !== 'moderateur') return false;
        return permissions.some(p => p.startsWith(`${module}:`));
    }, [typeUser, permissions]);

    return { hasPermission, hasAllPermissions, canAccessModule, typeUser, permissions };
};